import React, { useState } from "react";
import { ArrowForward } from "@mui/icons-material";
import { TopBarRedesigned, AdditionalActionsIconType } from "components/TopBarRedesigned";
import DentistSideMenu from "components/DentistSideMenu";
import { useHistory } from 'react-router';
import { 
  MainContainer,
  ContentContainer,
  PageTitle,
  VersionContainer,
  IntroText,
  StyledRadioGroup,
  VersionPaper,
  VersionHeader,
  StyledFormControlLabel,
  StyledRadio,
  VersionTitleContainer,
  VersionTitle,
  VersionContent,
  ButtonContainer,
  ContinueButton,
  BackButton,
  FooterContainer,
  FooterText,
  FooterLink
} from "./ChooseDescriptionStep.styles";
import { Box, Button } from "@mui/material";

export interface ChooseDescriptionStepProps {
  isOpen: boolean;
  spellCheckedResponse: string;
  finalResponse: string;
  onBack: () => void;
  onSave: (value: string) => void;
}

enum DescriptionVersion {
  SpellChecked = "spellChecked",
  Final = "final"
}

const ChooseDescriptionStep: React.FC<ChooseDescriptionStepProps> = ({
  isOpen,
  spellCheckedResponse,
  finalResponse,
  onBack,
  onSave,
}) => {
  const history = useHistory();
  const [selectedVersion, setSelectedVersion] = useState<DescriptionVersion>(DescriptionVersion.SpellChecked);
  // T2-6758: hamburger + side menu
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);

  const handleVersionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedVersion(event.target.value as DescriptionVersion);
  };

  const handleSave = () => {
    if (selectedVersion === DescriptionVersion.SpellChecked) {
      onSave(spellCheckedResponse);
    } else {
      onSave(finalResponse);
    }
  };

  return (
    <MainContainer>
      <TopBarRedesigned
        onBack={onBack}
        additionalActionsIconType={AdditionalActionsIconType.menu}
        onAdditionalActionsButtonClick={() => setIsSideMenuOpen(true)}
      />
      <DentistSideMenu isOpen={isSideMenuOpen} onClose={() => setIsSideMenuOpen(false)} />
      <ContentContainer>
        <PageTitle variant="h5">
          Post permanent or contract positions!
        </PageTitle>

        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box sx={{ 
            width: 10, 
            height: 10, 
            bgcolor: "#4CAF50", 
            borderRadius: "50%",
            display: "inline-block"
          }} />
          <IntroText variant="body2" sx={{ ml: 1, fontWeight: "medium" }}>
            Step 2 of 2
          </IntroText>
        </Box>

        <Box sx={{ 
          width: "100%", 
          height: "4px", 
          bgcolor: "#e0e0e0", 
          mb: 4, 
          borderRadius: 2,
          position: "relative",
          overflow: "hidden" 
        }}>
          <Box sx={{ 
            position: "absolute", 
            width: "100%", 
            height: "100%", 
            bgcolor: "var(--primary-blue)",
            borderRadius: 2
          }} />
        </Box>

        <VersionContainer>
          <IntroText variant="body1">
            Ok, nice! We've got 2 versions of your Job Description — you can pick your favourite, or go back and edit your original description:
          </IntroText>

          <StyledRadioGroup
            value={selectedVersion}
            onChange={handleVersionChange}
          >
            <VersionPaper elevation={1}>
              <VersionHeader>
                <StyledFormControlLabel 
                  value={DescriptionVersion.SpellChecked}
                  control={<StyledRadio />} 
                  label=""
                />
                <VersionTitleContainer>
                  <VersionTitle variant="h6">
                    Version #1 - Minor clean-up (spelling/grammar corrections, contact information removed)
                  </VersionTitle>
                  <VersionContent>
                    {spellCheckedResponse}
                  </VersionContent>
                </VersionTitleContainer>
              </VersionHeader>
            </VersionPaper>

            <VersionPaper elevation={1}>
              <VersionHeader>
                <StyledFormControlLabel 
                  value={DescriptionVersion.Final}
                  control={<StyledRadio />} 
                  label=""
                />
                <VersionTitleContainer>
                  <VersionTitle variant="h6">
                    Version #2 - Enhanced Writing Version (we cleaned it up as needed, and added some flair and personality to your posting)
                  </VersionTitle>
                  <VersionContent>
                    {finalResponse}
                  </VersionContent>
                </VersionTitleContainer>
              </VersionHeader>
            </VersionPaper>
          </StyledRadioGroup>
        </VersionContainer>
        
        <ButtonContainer>
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleSave}
            sx={{ 
              bgcolor: "var(--primary-blue)",
              borderRadius: 10,
              py: 1.5,
              textTransform: "uppercase",
              fontWeight: "bold",
              boxShadow: 2,
              width: "100%",
              '&:hover': {
                bgcolor: "#002266",
              }
            }}
          >
            Continue
          </Button>
          <Button 
            variant="text"
            onClick={onBack}
            sx={{
              color: "var(--primary-blue)",
              textTransform: "none",
            }}
          >
            go back and edit
          </Button>
        </ButtonContainer>
        
        <FooterContainer>
          <FooterText variant="body2">
            This is a new feature we're testing — please <FooterLink href="#">report any problems or feedback here</FooterLink> so we can make any necessary improvements.
          </FooterText>
        </FooterContainer>
      </ContentContainer>
    </MainContainer>
  );
};

export default ChooseDescriptionStep;

